/* Progressive enhancement for original content views. Copy lives in Shopify pages/sections;
   product and cart values are rendered from current native Shopify objects. */
(() => {
  'use strict';
  const roots = scope => Array.from(scope.querySelectorAll('[data-bns-content]'));
  const money = cents => new Intl.NumberFormat('en-US', { style: 'currency', currency: window.BNS?.currency || 'USD' }).format(Number(cents || 0) / 100);
  const visiblePrices = () => document.documentElement.dataset.mode !== 'wholesale' || Boolean(window.BNS?.wholesale);
  const records = root => Array.from(root.querySelectorAll('[data-bns-source-rows] [data-source-style]')).map(node => ({
    node, style: node.dataset.sourceStyle, displayStyle: node.dataset.displayStyle || node.dataset.sourceStyle, name: node.dataset.name, url: node.dataset.url,
    category: node.dataset.category, pack: node.dataset.pack, colours: Number(node.dataset.colours || 0), variantCount: Number(node.dataset.variantCount || 0),
    sizes: (node.dataset.sizes || '').split('|').filter(Boolean), order: Number(node.dataset.order || 0),
    gsm: Number(node.dataset.gsm || 0), oz: node.dataset.oz, material: node.dataset.material, capacity: node.dataset.capacity, image: node.dataset.image
  })).sort((a, b) => a.order - b.order);
  const animateNumber = (node, value) => {
    node.dataset.count = String(value);
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { node.textContent = value.toLocaleString(); return; }
    const start = performance.now();
    function frame(time) { const t = Math.min(1, (time - start) / 1100); node.textContent = Math.round(value * (1 - (1 - t) ** 3)).toLocaleString(); if (t < 1) requestAnimationFrame(frame); }
    requestAnimationFrame(frame);
  };
  function renderCatalogue(root) {
    const rows = records(root);
    for (const table of root.querySelectorAll('[data-bns-table]')) {
      const body = table.tBodies[0];
      Array.from(body.rows).sort((a, b) => Number(a.dataset.order) - Number(b.dataset.order)).forEach(row => body.append(row));
      if (table.dataset.bnsTable === 'sizes') {
        for (const head of table.querySelectorAll('th[data-size]')) {
          const cells = Array.from(table.querySelectorAll('td[data-size]')).filter(cell => cell.dataset.size === head.dataset.size);
          if (!cells.some(cell => cell.querySelector('.yes'))) { head.remove(); cells.forEach(cell => cell.remove()); }
        }
      }
    }
    const categories = (root.dataset.categoryOrder || '').split('|').filter(Boolean);
    const summary = root.querySelector('[data-bns-category-summary]');
    if (summary) summary.textContent = categories.map(category => `${category} (${rows.filter(row => row.category === category).length})`).join(' · ');
    const tbody = root.querySelector('[data-bns-category-table] tbody');
    if (tbody) {
      tbody.replaceChildren();
      categories.forEach(category => {
        const list = rows.filter(row => row.category === category); if (!list.length) return;
        const tr = document.createElement('tr'), label = tr.insertCell(), packs = tr.insertCell(), styles = tr.insertCell();
        const strong = document.createElement('b'); strong.textContent = category; label.append(strong);
        packs.className = 'n'; packs.textContent = Array.from(new Set(list.map(row => row.pack))).join(' / ');
        list.forEach((row, index) => { if (index) styles.append(', '); const link = document.createElement('a'); link.href = row.url; link.textContent = row.displayStyle; styles.append(link); });
        tbody.append(tr);
      });
    }
    const deepest = rows.find(row => row.style === 'H1003');
    const heaviest = rows.reduce((best, row) => row.gsm > (best?.gsm || 0) ? row : best, null);
    for (const [kind, product] of [['deepest', deepest], ['heaviest', heaviest]]) {
      if (!product) continue;
      const style = root.querySelector(`[data-bns-${kind}-style]`), link = root.querySelector(`[data-bns-${kind}-link]`);
      if (style) style.textContent = product.displayStyle;
      if (link) link.href = product.url;
      const description = root.querySelector(`[data-bns-${kind}-description]`);
      if (description) description.textContent = kind === 'deepest' ? `${product.colours} colourways across ${product.sizes.length} sizes.` : `${product.gsm} GSM, ${product.oz ? product.oz + ' oz' : ''}.`;
    }
    const totals = { styles: rows.length, colours: rows.reduce((n, row) => n + row.colours, 0), skus: rows.reduce((n, row) => n + row.variantCount, 0) };
    root.querySelectorAll('[data-count]').forEach(node => animateNumber(node, totals[node.dataset.bnsStat] ?? Number(node.dataset.count)));
  }
  async function completeCatalogue(root) {
    let next = root.dataset.nextPage; const visited = new Set();
    while (next && !visited.has(next) && visited.size < 20) {
      visited.add(next);
      const url = new URL(next, window.location.href);
      if (url.origin !== window.location.origin) throw new Error('Unexpected catalogue origin');
      url.searchParams.set('section_id', root.dataset.sectionId);
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Catalogue page unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = doc.querySelector('[data-bns-content]');
      if (!incoming || incoming.dataset.bnsContent !== root.dataset.bnsContent) throw new Error('Unexpected catalogue section');
      const target = root.querySelector('[data-bns-source-rows]');
      const known = new Set(Array.from(target?.children || []).map(node => node.dataset.sourceStyle));
      incoming.querySelectorAll('[data-bns-source-rows] [data-source-style]').forEach(node => { if (!known.has(node.dataset.sourceStyle)) { target.append(document.importNode(node, true)); known.add(node.dataset.sourceStyle); } });
      for (const table of root.querySelectorAll('[data-bns-table]')) {
        const peer = Array.from(incoming.querySelectorAll('[data-bns-table]')).find(candidate => candidate.dataset.bnsTable === table.dataset.bnsTable);
        const present = new Set(Array.from(table.tBodies[0].rows).map(row => row.dataset.style));
        Array.from(peer?.tBodies[0]?.rows || []).forEach(row => { if (!present.has(row.dataset.style)) { table.tBodies[0].append(document.importNode(row, true)); present.add(row.dataset.style); } });
      }
      next = incoming.dataset.nextPage;
    }
    if (next) throw new Error('Catalogue pagination did not finish');
    root.querySelector('[data-bns-content-more]')?.remove();
    renderCatalogue(root);
  }
  function quote(root) {
    const body = root.querySelector('[data-bns-quote-body]'), summary = root.querySelector('[data-bns-quote-summary]');
    if (!body || !summary || body.value.trim() || body.dataset.edited) return;
    let cart = { items: [], total: 0 }; try { cart = JSON.parse(root.querySelector('[data-bns-quote-cart]')?.textContent || '{}'); } catch (_) { return; }
    const items = cart.items || [], pieces = items.reduce((sum, item) => sum + item.units * item.pack, 0);
    const labels = summary.dataset, totalLabel = () => labels.summaryTemplate.replace('{pieces}', pieces).replace('{lines}', items.length);
    let text = items.map(item => `${item.style}  ${item.name}  ·  ${item.variant}  ·  ${item.units * item.pack} ${labels.piecesLabel}${visiblePrices() ? '  ·  ' + money(item.line_price) : ''}`).join('\n');
    if (items.length) text += `\n\n${totalLabel()}${visiblePrices() ? '  ·  ' + labels.merchandiseLabel + ' ' + money(cart.total) : ''}`;
    const params = new URLSearchParams(location.search), style = params.get('style'), handle = params.get('product');
    const product = records(root).find(row => (style && row.style.toLowerCase() === style.toLowerCase()) || (handle && row.url.split('/').pop() === handle));
    if (product) {
      text = `${product.style} — ${product.name}${product.capacity ? ' (' + product.capacity + ')' : ''}${product.pack ? '\n' + labels.packLabel + ': ' + product.pack + ' ' + labels.piecesLabel : ''}\n${labels.quantityLabel}: ${text ? '\n\n' + text : ''}`;
      summary.querySelector('[data-quote-heading]').textContent = product.name;
      summary.querySelector('[data-quote-detail]').textContent = `${labels.modelLabel} ${product.style} · ${product.material || ''}${product.capacity ? ' · ' + product.capacity : ''}${summary.dataset.productSuffix}`;
      if (product.image) { const image = summary.querySelector('[data-bns-quote-image]').content.firstElementChild.cloneNode(true); image.src = product.image; summary.prepend(image); }
      summary.querySelector('svg').setAttribute('hidden', '');
      const link = summary.querySelector('[data-quote-link]'); link.href = product.url; link.textContent = labels.productLinkLabel;
    } else if (items.length) {
      summary.querySelector('[data-quote-heading]').textContent = summary.dataset.cartHeading;
      summary.querySelector('[data-quote-detail]').textContent = `${totalLabel()}${visiblePrices() ? ' · ' + money(cart.total) + ' ' + labels.merchandiseLabel : ''}${summary.dataset.cartSuffix}`;
    }
    if (product || items.length) { body.value = text; body.rows = Math.min(14, items.length + 4); summary.hidden = false; }
  }
  function wholesaleApplication(root) {
    const box = root.querySelector('[data-wholesale-application]');
    const form = box?.matches('form') ? box : box?.querySelector('form');
    if (!box || !form || box.dataset.waBound) return;
    box.dataset.waBound = 'true';
    const fileInput = box.querySelector('[data-wholesale-certificate]');
    const nativeSubmit = form.submit.bind(form);
    let sending = null;
    const send = () => {
      if (sending) return sending;
      const endpoint = (box.dataset.endpoint || '').trim();
      if (!endpoint) return;
      let parsed;
      try { parsed = new URL(endpoint, window.location.href); } catch (_) { return; }
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return;
      const file = fileInput?.files?.[0];
      let body;
      let keepalive = true;
      if (file) {
        body = new FormData();
        for (const [key, value] of new FormData(form)) {
          if (typeof value === 'string') body.append(key, value);
        }
        body.append('certificate', file, file.name);
        keepalive = false;
      } else {
        body = new URLSearchParams();
        for (const [key, value] of new FormData(form)) {
          if (typeof value === 'string') body.append(key, value);
        }
      }
      sending = fetch(parsed.href, { method: 'POST', body, keepalive, credentials: 'omit', mode: 'cors' }).catch(() => {});
      return sending;
    };
    const finish = () => {
      if (box.dataset.waNative) return;
      box.dataset.waNative = 'true';
      nativeSubmit();
    };
    form.addEventListener('submit', event => {
      if (fileInput?.files?.[0]) {
        event.preventDefault();
        Promise.resolve(send()).then(finish);
        return;
      }
      send();
    });
    form.submit = function patchedSubmit() {
      return Promise.resolve(send()).then(finish);
    };
  }
  function init(root) {
    if (root.dataset.contentReady) return; root.dataset.contentReady = 'true';
    root.querySelector('[data-bns-quote-body]')?.addEventListener('input', event => { event.currentTarget.dataset.edited = 'true'; });
    wholesaleApplication(root);
    if (root.querySelector('[data-bns-source-rows]')) completeCatalogue(root).then(() => quote(root)).catch(() => { renderCatalogue(root); quote(root); });
  }
  const start = scope => roots(scope).forEach(init);
  start(document);
  document.addEventListener('shopify:section:load', event => start(event.target));
})();
